import React, { useRef } from 'react';
import { socket } from '../../../../lib/socket';
import { useChat } from '../../../../store/slices/chat';
import TextArea from 'antd/es/input/TextArea';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onEnterPress?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const MessageInput = ({
  value,
  onChange,
  disabled = false,
  onEnterPress,
}: MessageInputProps) => {
  const textareaRef = useRef<TextAreaRef>(null);
  const { selectedRoomId } = useChat();
  const userId = useSelector(
    (state: RootState) => state.auth.user?.adminData?.id
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Completely block any changes when disabled
    if (disabled) {
      e.preventDefault();
      return;
    }

    socket.emit('chat-events', {
      type: 'typing',
      senderId: userId,
      roomId: selectedRoomId,
      isTyping: true,
    });
    onChange(e.target.value);
  };

  const handleBlur = () => {
    if (disabled) return;

    socket.emit('chat-events', {
      type: 'typing',
      senderId: userId,
      roomId: selectedRoomId,
      isTyping: false,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (disabled || !onEnterPress) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onEnterPress(e);
    }
  };

  return (
    <div className="flex-1">
      <div className="max-w-full flex-1 break-words">
        <TextArea
          ref={textareaRef}
          className="flex-1 border-0 shadow-none h-full bg-transparent hover:bg-transparent focus-within:shadow-none focus-within:bg-transparent text-light-text-primary dark:text-dark-text-primary placeholder:text-light-text-placeholder dark:placeholder:text-dark-text-placeholder disabled:cursor-not-allowed disabled:opacity-50"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoSize={{ minRows: 1, maxRows: 10 }}
          placeholder={
            disabled ? 'This chat is closed' : 'write your message...'
          }
          disabled={disabled}
          readOnly={disabled}
        />
      </div>
    </div>
  );
};

export default MessageInput;
